import { useParams, Link } from 'react-router-dom'
import { useTopicsStore, useCardsStore } from '@/stores'
import CardTree from '@/components/CardTree'
import './TopicPage.css'

export default function TopicPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const { getTopic, isLoading: topicsLoading } = useTopicsStore()
  const { getCardTree, getCardsByTopic, isLoading: cardsLoading } = useCardsStore()

  const isLoading = topicsLoading || cardsLoading
  const topic = topicId ? getTopic(topicId) : undefined
  const cardTree = topicId ? getCardTree(topicId) : []
  const topicCards = topicId ? getCardsByTopic(topicId) : []

  if (isLoading) {
    return (
      <div className="topic-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!topic) {
    return (
      <div className="empty-state">
        <h3>Topic not found</h3>
        <p>The topic you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  return (
    <div className="topic-page">
      <div className="page-header">
        <div>
          <div className="topic-breadcrumb">
            <Link to="/dashboard">Dashboard</Link>
            <span className="breadcrumb-sep">/</span>
            <span>Topic {topic.id}</span>
          </div>
          <h1>
            <span className="topic-id-badge">{topic.id}/</span>
            {topic.name}
          </h1>
          {topic.description && (
            <p className="text-secondary">{topic.description}</p>
          )}
        </div>
        <div className="topic-page__actions">
          <Link to={`/topic/${topicId}/canvas`} className="btn btn-secondary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="5" cy="6" r="2" />
              <circle cx="12" cy="6" r="2" />
              <circle cx="19" cy="18" r="2" />
              <circle cx="12" cy="18" r="2" />
              <path d="M5 8v8c0 2 1 4 7 4M12 8v4M19 16v-6c0-2-1-4-7-4" />
            </svg>
            Canvas View
          </Link>
          <Link to={`/card/new?topic=${topicId}`} className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Card
          </Link>
        </div>
      </div>

      <div className="topic-stats">
        <div className="stat-item">
          <span className="stat-value">{topicCards.length}</span>
          <span className="stat-label">Cards</span>
        </div>
      </div>

      {cardTree.length === 0 ? (
        <div className="empty-state">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <path d="M12 8v8M8 12h8" />
          </svg>
          <h3>No cards yet</h3>
          <p>Create your first card in this topic to start building your knowledge tree.</p>
          <Link to={`/card/new?topic=${topicId}`} className="btn btn-primary">
            Create First Card
          </Link>
        </div>
      ) : (
        <div className="card-tree-container card">
          <CardTree nodes={cardTree} />
        </div>
      )}
    </div>
  )
}
